import { DestroyRef, inject, Injectable, InjectionToken, type Signal, signal } from '@angular/core';

import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { firstValueFrom, Subject } from 'rxjs';
import { io } from 'socket.io-client';

import { environment } from '../../../../environments/environment';
import { parseAgentEvent } from '../../../shared/utils/leapbot-chat.util';
import { LEAPBOT_RETRY_DELAYS_MS } from '../../config/leapbot.config';
import type { LeapBotAgentEvent, LeapBotConnectionStatus } from '../../interfaces/leapbot.interface';

/** The slice of a Socket.IO client the service uses; lets tests inject a fake. */
export interface LeapBotSocketLike {
  connected: boolean;
  on(event: string, handler: (...args: unknown[]) => void): unknown;
  emit(event: string, ...args: unknown[]): unknown;
  disconnect(): unknown;
  removeAllListeners(): unknown;
}

/** Options Fury passes when opening the answer channel. */
export interface LeapBotSocketOptions {
  path: string;
  transports: string[];
  auth: { token: string };
  query: { threadId: string };
  reconnection: false;
}

/** Creates the Socket.IO client for the answer channel. */
export type LeapBotSocketFactory = (url: string, options: LeapBotSocketOptions) => LeapBotSocketLike;

/** Factory of the answer-channel socket; replaced by a fake in tests. */
export const LEAPBOT_SOCKET_FACTORY = new InjectionToken<LeapBotSocketFactory>('LEAPBOT_SOCKET_FACTORY', {
  providedIn: 'root',
  factory: (): LeapBotSocketFactory => (url, options) => io(url, options),
});

/** An error reported by LeapBot over the channel (`system_error`). */
export interface LeapBotChannelError {
  code: string;
  message: string;
}

const CONNECT_TIMEOUT_MS = 20_000;

/**
 * Owns the Socket.IO channel on which LeapBot streams answers for one conversation at a time.
 *
 * The user's Auth0 access token is read fresh on every connection; nothing is cached here.
 * Reconnection only reopens the channel: a message already accepted by LeapBot is never re-sent.
 */
@Injectable({ providedIn: 'root' })
export class LeapBotSocketService {
  private readonly auth0 = inject(Auth0Service);
  private readonly createSocket = inject(LEAPBOT_SOCKET_FACTORY);
  private socket: LeapBotSocketLike | null = null;
  private currentThreadId: string | null = null;
  private readonly statusState = signal<LeapBotConnectionStatus>('disconnected');

  /** Connection state of the answer channel. */
  readonly status: Signal<LeapBotConnectionStatus> = this.statusState.asReadonly();

  private readonly agentEventsSubject = new Subject<LeapBotAgentEvent>();
  private readonly channelErrorsSubject = new Subject<LeapBotChannelError>();
  private readonly droppedSubject = new Subject<void>();

  /** Known agent events (`TokenStream`, `ToolStart`, ...); unknown or malformed ones are dropped. */
  readonly agentEvents$ = this.agentEventsSubject.asObservable();
  /** `system_error` events from LeapBot. */
  readonly channelErrors$ = this.channelErrorsSubject.asObservable();
  /** Emits when an open channel is lost without the client asking for it. */
  readonly dropped$ = this.droppedSubject.asObservable();

  constructor() {
    inject(DestroyRef).onDestroy(() => this.disconnect());
  }

  /** Whether the channel is open for the given conversation. */
  isConnectedTo(threadId: string): boolean {
    return this.currentThreadId === threadId && this.socket?.connected === true;
  }

  /**
   * Opens the channel for a conversation, retrying up to three times (1.5 s, 3 s, 4.5 s) while
   * the status stays `connecting`; after the last failure the status is `error` and it rejects.
   */
  async connect(threadId: string): Promise<void> {
    if (this.isConnectedTo(threadId)) return;
    this.disconnect();
    this.statusState.set('connecting');
    let lastError: unknown = null;
    for (let attempt = 0; attempt <= LEAPBOT_RETRY_DELAYS_MS.length; attempt++) {
      if (attempt > 0) await delay(LEAPBOT_RETRY_DELAYS_MS[attempt - 1]);
      try {
        await this.openOnce(threadId);
        this.statusState.set('connected');
        return;
      } catch (error) {
        lastError = error;
        this.closeSocket();
      }
    }
    this.statusState.set('error');
    // The chat only shows a generic message; the real reason (never the token) goes to the console for support.
    console.warn('[LeapBot] The answer channel could not be opened:', lastError instanceof Error ? lastError.message : lastError);
    throw lastError instanceof Error ? lastError : new Error('LeapBot channel unavailable');
  }

  /** Sends a follow-up text over the open channel. */
  sendMessage(message: string): void {
    if (!this.socket?.connected) throw new Error('LeapBot channel is not connected');
    this.socket.emit('send_message', { message, processId: crypto.randomUUID() });
  }

  /** Asks LeapBot to stop the answer in progress. */
  abort(): void {
    if (this.socket?.connected) this.socket.emit('abort_generation');
  }

  /** Closes the channel without emitting `dropped$`. */
  disconnect(): void {
    this.closeSocket();
    this.currentThreadId = null;
    this.statusState.set('disconnected');
  }

  private closeSocket(): void {
    const socket = this.socket;
    this.socket = null;
    if (!socket) return;
    socket.removeAllListeners();
    socket.disconnect();
  }

  private async openOnce(threadId: string): Promise<void> {
    const config = environment.leapbot;
    if (!config) throw new Error('LeapBot is not configured');
    const audience = config.audience ?? environment.auth0.audience;
    const token = await firstValueFrom(
      this.auth0.getAccessTokenSilently({ authorizationParams: { audience } }),
    );

    await new Promise<void>((resolve, reject) => {
      const socket = this.createSocket(new URL(config.apiUrl).origin, {
        path: '/ws',
        transports: ['websocket'],
        auth: { token },
        query: { threadId },
        reconnection: false,
      });
      this.socket = socket;
      this.currentThreadId = threadId;
      let ready = false;
      const timer = setTimeout(() => reject(new Error('LeapBot channel timeout')), CONNECT_TIMEOUT_MS);
      const settle = (fn: () => void): void => {
        clearTimeout(timer);
        fn();
      };

      socket.on('connection_ready', () => {
        ready = true;
        settle(resolve);
      });
      socket.on('agent_event', (payload) => {
        const event = parseAgentEvent(payload);
        if (event) this.agentEventsSubject.next(event);
      });
      socket.on('system_error', (payload) => {
        const error = toChannelError(payload);
        console.warn('[LeapBot] system_error:', error.code, error.message);
        this.channelErrorsSubject.next(error);
        if (!ready) settle(() => reject(new Error(error.message)));
      });
      socket.on('app_heartbeat', (...args) => {
        const ack = args[args.length - 1];
        if (typeof ack === 'function') ack();
      });
      socket.on('connect_error', (error) => {
        settle(() => reject(error instanceof Error ? error : new Error('LeapBot connect error')));
      });
      socket.on('disconnect', (reason) => {
        if (!ready) return;
        this.statusState.set('disconnected');
        if (reason !== 'io client disconnect') this.droppedSubject.next();
      });
    });
  }
}

function toChannelError(payload: unknown): LeapBotChannelError {
  if (typeof payload === 'object' && payload !== null) {
    const { code, message } = payload as { code?: unknown; message?: unknown }; // LeapBot `system_error` payload
    return {
      code: typeof code === 'string' ? code : 'UNKNOWN',
      message: typeof message === 'string' ? message : '',
    };
  }
  return { code: 'UNKNOWN', message: '' };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
